import { RegistrationApi } from '@app/services/apiServices/RegistrationApi';
import { NetworkErrorService } from '@app/services/NetworkError.service';
import { RegistrationStore } from '@app/pages/registration/stores/RegistrationStore';
import { FeaturesService } from '@app/services/Features.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { take } from "rxjs/operators";
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output
} from "@angular/core";
import { WindowToken } from "@app/util/window.token";
import { MatDialog } from "@angular/material/dialog";
import { PersonalInfoData } from "@app/components/personalInfo/personalInfo.directive";
import { Router } from "@angular/router";

export class Email implements OnInit {

    @Input() personalInfoData: PersonalInfoData;

    @Output() invalidCheck = new EventEmitter<Object>();

    emailValue = "";
    showEasyEmailAddressInput: boolean = false;

    constructor(
        @Inject(RegistrationApi) private registrationApi: RegistrationApi,
        @Inject(WindowToken) private $window: any,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(RegistrationStore) private registrationStore: RegistrationStore,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(FeaturesService) private featuresService: FeaturesService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(Router) private router: Router
    ) {
    }

    ngOnInit() {
        const homeowner = this.registrationStore.getHomeowner();

        // for validating and setting the pre-populated fields from URL params
        if (homeowner != null) {
            this.personalInfoData.homeowner = homeowner;
        }

        if (this.personalInfoData.registrationType === 'easy') {
            this.emailLookup();
        } else if (this.personalInfoData.homeowner !== undefined) {
            this.personalInfoData.homeowner.contactInformation.email.value ? this.emailLookup() : null;
        }

        this.emailValue = "";
        if (this.personalInfoData &&
            this.personalInfoData.homeowner &&
            this.personalInfoData.homeowner.contactInformation &&
            this.personalInfoData.homeowner.contactInformation.email) {
            this.emailValue = this.personalInfoData.homeowner.contactInformation.email.value;
        }
        this.personalInfoData.emailAddressErrorMessage = "Please enter your email address.";
        this.personalInfoData.accountActive = true;
    }

    validateEmail(email) {
        if (email != null && email.length > 0) {
            const regex = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

            return regex.test(email);
        }
        return true;
    }

    emailLookup() {

        if (this.emailValue && this.emailValue !== this.personalInfoData.homeowner?.contactInformation?.email?.value) {
            this.personalInfoData.homeowner.contactInformation.email.value = this.emailValue;
        }

        this.personalInfoData.emailAddressError = false;
        this.personalInfoData.emailAddressExists = false;
        this.personalInfoData.emailAddressErrorMessage = null;
        this.personalInfoData.accountActive = true;
        this.personalInfoData.isHoaHomeowner = true;
        this.personalInfoData.isValidatingEmail = true;
        this.personalInfoData.emailAddressLookupExecuted = false;

        this.emailAddressLookup().finally(() => {
            if (this.personalInfoData.registrationType === 'easy' && this.personalInfoData.emailAddressError){
                this.showEasyEmailAddressInput = true;
            }
            this.personalInfoData.isValidatingEmail = false;
        });
    }

    emailAddressLookup(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.personalInfoData.homeowner &&
                this.personalInfoData.homeowner.contactInformation &&
                this.personalInfoData.homeowner.contactInformation.email &&
                this.personalInfoData.homeowner.contactInformation.email.value) {
                this.invalidCheck.emit({ invalidObj: "Validating Email." });
                this.registrationApi.emailValidation(this.personalInfoData.homeowner.contactInformation.email.value)
                    .pipe(take(1)).subscribe((data) => {
                        this.invalidCheck.emit({ invalidObj: false });
                        if (data.success) {
                            if (!this.validateEmail(this.personalInfoData.homeowner.contactInformation.email.value)) {
                                this.invalidCheck.emit({ invalidObj: "Invalid email address." });
                                this.personalInfoData.emailAddressErrorMessage = "Please enter a valid email address.";
                                this.personalInfoData.emailAddressError = true;
                                reject();
                            } else {
                                this.invalidCheck.emit(null);
                                // MMDP-1970: removed until Registered User functionality is enabled.
                                if (this.personalInfoData.registeredUserEnabled) {
                                    this.personalInfoData.accountActive = true;
                                    this.registrationApi.emailAddressExists(this.personalInfoData.homeowner.contactInformation.email.value, true)
                                        .pipe(take(1)).subscribe((data) => {
                                            if (data.amuiBaseUser && data.amuiBaseUser.status !== 'ACTIVE') {
                                                this.personalInfoData.accountActive = false;
                                                this.personalInfoData.isHoaHomeowner = false;
                                                this.personalInfoData.emailAddressErrorMessage = "This email address entered indicates you have an inactive account.";
                                                if (this.personalInfoData.showErrorMessage) {
                                                    this.personalInfoData.emailAddressError = true;
                                                }
                                                reject();
                                            } else {
                                                if (data.matchFound === true && data.hoaHomeowner === true) {
                                                //email address exists and is already a hoahomeowner
                                                    this.personalInfoData.emailAddressExists = true;
                                                    this.personalInfoData.isHoaHomeowner = true;
                                                    this.registrationStore.setHoaHomeowner(true);
                                                    this.registrationStore.setAmuiUser(true);
                                                    this.personalInfoData.emailAddressErrorMessage = "This email address entered indicates you are a registered user.";
                                                    if (this.personalInfoData.showErrorMessage) {
                                                        this.personalInfoData.emailAddressError = true;
                                                    }
                                                    reject();
                                                } else if (data.matchFound === true && data.hoaHomeowner === false) {
                                                //email address exists but is not a hoahomeowner
                                                    this.personalInfoData.emailAddressExists = true;
                                                    this.personalInfoData.isHoaHomeowner = false;
                                                    this.registrationStore.setHoaHomeowner(false);
                                                    this.registrationStore.setAmuiUser(true);
                                                    this.personalInfoData.emailAddressErrorMessage = "This email address entered indicates you already " +
                                                    "exist in our authentication system, please restart the registration process with this email " +
                                                    "to complete registration.";
                                                    if (this.personalInfoData.showErrorMessage) {
                                                        this.personalInfoData.emailAddressError = true;
                                                    }
                                                    reject();
                                                } else {
                                                    this.personalInfoData.isHoaHomeowner = false;
                                                    this.registrationStore.setHoaHomeowner(false);
                                                    this.registrationStore.setAmuiUser(false);
                                                    resolve();
                                                }
                                            }

                                            this.personalInfoData.emailAddressLookupExecuted = true;
                                        }, (err) => {
                                            this.personalInfoData.isHoaHomeowner = false;
                                            this.registrationStore.setHoaHomeowner(false);
                                            this.registrationStore.setAmuiUser(false);
                                            let errMsg: string = undefined;
                                            if (err.error && err.error.message) {
                                                errMsg = err.error.message;
                                            }
                                            this.errorHandlerService.handleNetworkError(err, true, errMsg)
                                                .pipe(take(1)).subscribe(() => {}, () => {});
                                            reject();
                                        });
                                }
                            }
                        } else {
                            this.invalidCheck.emit({ invalidObj: "Invalid email address." });
                            this.personalInfoData.emailAddressErrorMessage = "Please enter a valid email address.";
                            this.personalInfoData.emailAddressError = true;
                            reject();
                        }
                    }, (err) => {
                        this.invalidCheck.emit({ invalidObj: false });
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.emailLookup();
                                }
                            });
                        reject();
                    });
            } else {
                this.personalInfoData.emailAddressErrorMessage = "Please enter a valid email address.";
                this.invalidCheck.emit({ invalidObj: "invalid format" });
                this.personalInfoData.emailAddressError = true;
                reject();
            }

        });
    }
}

@Component({
    selector: 'app-email',
    template: require('./email.directive.html')
})
export class EmailComponent extends Email {}

@Component({
    selector: 'app-email-easy',
    template: require('./emailEasy.directive.html')
})
export class EmailEasyComponent extends Email {}
